import React, { Fragment, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Menu, Transition } from "@headlessui/react";
import classNames from "classnames";
import { usePopper } from "react-popper";
import { twClassNames } from "../../helpers/twClassNames";

interface Theme {
    activeItemClasses: string;
    inactiveItemClasses: string;
    disabledItemClasses: string;
    selectedItemClasses: string;
    menuButtonClasses: string;
    menuButtonIconClasses: string;
    menuButtonTextClasses: string;
    menuItemContainerClasses: string;
}
export function getTheme(
    lightMode,
    fullWidth = false,
    limitMenuWidth = false,
    iconOnly = false,
    menuButtonSize = "medium",
    icon = "",
    maxHeight?: string
): Theme {
    const darkMode = !lightMode;

    const itemStateClasses = "menu-item";
    const menuBtnBaseClasses = "btn2 px-3 group font-normal";
    const menuItemBaseClasses = "top-full mt-1 right-0";

    let baseIconClasses = "far transition duration-200 " + (icon || "fa-chevron-down");
    if (!iconOnly) {
        baseIconClasses += " scale-75 opacity-75";
    }

    return {
        activeItemClasses: classNames(itemStateClasses, {
            "active": lightMode,
            "active dark": darkMode
        }),
        inactiveItemClasses: classNames(itemStateClasses, {
            "inactive": lightMode,
            "inactive dark": darkMode
        }),
        disabledItemClasses: classNames(itemStateClasses, "cursor-not-allowed", {
            "disabled": lightMode,
            "disabled dark": darkMode
        }),
        selectedItemClasses: classNames(itemStateClasses, {
            "selected": lightMode,
            "selected dark": darkMode
        }),
        menuButtonClasses: classNames(
            menuBtnBaseClasses,
            menuButtonSize,
            {
                "secondary text-secondary group-hover:!text-gray-600": lightMode,
                "dark-secondary !text-gray-400 group-hover:!text-gray-300": darkMode
            },
            fullWidth && "w-full"
        ),
        menuButtonTextClasses: classNames("truncate font-medium", {
            "text-gray-700": lightMode,
            "text-gray-200": darkMode
        }),
        menuButtonIconClasses: baseIconClasses,
        menuItemContainerClasses: classNames(
            menuItemBaseClasses,
            { "menu-container": lightMode, "menu-container dark": darkMode },
            limitMenuWidth ? "max-w-full break-all !w-full" : "min-w-full break-all",
            maxHeight && maxHeight,
            maxHeight && "overflow-y-auto"
        )
    };
}

export interface DropdownItem {
    text: string | React.ReactNode;
    value: string | number | boolean;
    disabled?: boolean;
    className?: string;
    menuItemParentClassName?: string;
    icon?: string;
    /** When true, this item is rendered as a collapsible group header. Clicking it toggles visibility of subsequent items until the next group header. */
    isGroupHeader?: boolean;
}

export interface DropdownProps {
    theme?: "light" | "dark";
    title?: string;
    titlePrefix?: React.ReactNode;
    titlePrefixClassName?: string;
    onItemSelected: (v: any, e: any) => void;
    items: DropdownItem[];
    icon?: string;
    disabled?: boolean;
    iconOnly?: boolean;
    fullWidth?: boolean;
    limitMenuWidth?: boolean;
    className?: string;
    autoSetTitle?: boolean;
    dropdownMenuStyling?: string;
    menuButtonSize?: "xxsmall" | "xsmall" | "small" | "medium" | "large" | "xlarge";
    defaultSelectedItem?: DropdownItem;
    onItemMouseEnter?: (item: any) => void;
    onItemMouseLeave?: (item: any) => void;
    error?: boolean;
    itemComponent?: React.FC;
    label?: string;
    /** Optional class name for the menu button so callers can override styling */
    buttonClassName?: string;
    /** Optional max height for the dropdown menu with scrolling. Pass Tailwind classes like "max-h-80" (320px), "max-h-96" (384px), or "max-h-[500px]". When provided, enables overflow scrolling. */
    maxHeight?: string;
    /** When true, renders the dropdown menu via a portal to document.body to escape overflow containers (e.g. modals). */
    usePortal?: boolean;
    /** When set, the menu closes on scroll or wheel events on this element (e.g. React Flow viewport) so the menu does not move with the scroll. */
    closeOnScrollRef?: React.RefObject<HTMLElement | null>;
    /** When true, adds a search input at the top of the dropdown menu to filter items. */
    searchable?: boolean;
    /** Placeholder text for the search input when searchable is true. */
    searchPlaceholder?: string;
}

export function DefaultDropdownItem(item: DropdownItem) {
    return (
        <span
            className={twClassNames(
                "flex items-baseline gap-2 truncate",
                item.disabled ? "cursor-not-allowed !text-gray-300" : "",
                item.menuItemParentClassName ? item.menuItemParentClassName : ""
            )}
        >
            {item.icon && <i className={item.icon}></i>}
            {item.text || item.value}
        </span>
    );
}

export function Dropdown(props: DropdownProps) {
    const [title, setTitle] = useState(props.defaultSelectedItem?.text || props.title);
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [frozenPopperStyle, setFrozenPopperStyle] = useState(null);
    const latestStylesRef = React.useRef(null);
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string | number | boolean>>(
        new Set()
    );

    const freezePosition = Boolean(props.usePortal && props.closeOnScrollRef);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: "bottom-start",
        strategy: freezePosition ? "fixed" : undefined,
        modifiers: [
            {
                name: "flip",
                options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"] }
            },
            { name: "preventOverflow", options: { boundary: document.body, padding: 8 } }
        ]
    });

    latestStylesRef.current = styles.popper;
    const menuStyle =
        freezePosition && frozenPopperStyle
            ? {
                  ...frozenPopperStyle,
                  transitionProperty: "opacity",
                  minWidth: (referenceElement as HTMLElement)?.offsetWidth ?? "auto"
              }
            : {
                  ...styles.popper,
                  ...(props.usePortal
                      ? {
                            transitionProperty: "opacity",
                            minWidth: (referenceElement as HTMLElement)?.offsetWidth ?? "auto"
                        }
                      : {})
              };

    const theme = getTheme(
        props.theme ? props.theme === "light" : true,
        props.fullWidth,
        props.limitMenuWidth,
        props.iconOnly,
        props.menuButtonSize || "medium",
        props.icon,
        props.maxHeight
    );

    const onClickItem = (item: any, e: React.MouseEvent | null = null) => {
        e?.stopPropagation();
        props.onItemSelected?.(item, e);
        if (props.autoSetTitle) {
            setTitle(item.text);
        }
    };

    const DropdownItemComponent = props.itemComponent || DefaultDropdownItem;

    useEffect(() => {
        if (props.defaultSelectedItem) {
            setTitle(props.defaultSelectedItem.text);
        }
    }, [props.defaultSelectedItem]);

    useEffect(() => {
        const el = props.closeOnScrollRef?.current;
        if (!el || !referenceElement) return;
        const closeMenu = () => {
            (referenceElement as HTMLElement)?.blur?.();
        };
        el.addEventListener("scroll", closeMenu, { passive: true });
        el.addEventListener("wheel", closeMenu, { passive: true });
        return () => {
            el.removeEventListener("scroll", closeMenu);
            el.removeEventListener("wheel", closeMenu);
        };
    }, [props.closeOnScrollRef, referenceElement]);

    useEffect(() => {
        if (!freezePosition) {
            if (frozenPopperStyle) setFrozenPopperStyle(null);
            return;
        }
        if (!popperElement) {
            setFrozenPopperStyle(null);
            return;
        }
        const id = setTimeout(() => {
            if (latestStylesRef.current) {
                setFrozenPopperStyle({ ...latestStylesRef.current });
            }
        }, 120);
        return () => clearTimeout(id);
    }, [freezePosition, popperElement]);

    const filteredItems = React.useMemo(() => {
        if (!props.searchable || !searchQuery) return props.items;

        const query = searchQuery.toLowerCase();

        const extractText = (node: React.ReactNode): string => {
            if (typeof node === "string") return node;
            if (typeof node === "number") return String(node);
            if (!node) return "";
            if (React.isValidElement(node)) {
                const { children } = node.props as { children?: React.ReactNode };
                return extractText(children);
            }
            if (Array.isArray(node)) return node.map(extractText).join(" ");
            return "";
        };

        const matchesSearch = (item: DropdownItem) => {
            const text = extractText(item.text);
            return text.toLowerCase().includes(query);
        };

        // Build a set of group header values that have at least one matching child
        const headersWithMatches = new Set<string | number | boolean>();
        let lastHeaderValue: string | number | boolean | null = null;
        for (const item of props.items) {
            if (item.isGroupHeader) {
                lastHeaderValue = item.value;
            } else if (matchesSearch(item) && lastHeaderValue !== null) {
                headersWithMatches.add(lastHeaderValue);
            }
        }

        return props.items.filter((item) => {
            if (item.isGroupHeader) return headersWithMatches.has(item.value);
            return matchesSearch(item);
        });
    }, [props.items, props.searchable, searchQuery]);

    const customButtonClasses = props.buttonClassName
        ? classNames(
              "btn2 px-3 group font-normal",
              props.menuButtonSize || "medium",
              props.fullWidth && "w-full",
              props.buttonClassName
          )
        : null;

    const dropdownMenu = (
        <Transition
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => {
                if (props.searchable) setSearchQuery("");
                setCollapsedGroups(new Set());
            }}
        >
            <Menu.Items
                className={classNames(
                    theme.menuItemContainerClasses,
                    props.dropdownMenuStyling || null,
                    props.usePortal && "!z-[9999]"
                )}
                ref={setPopperElement}
                style={menuStyle}
                {...attributes.popper}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                onMouseUp={(e: React.MouseEvent) => e.stopPropagation()}
            >
                {props.searchable && (
                    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-gray-400"
                            placeholder={props.searchPlaceholder || "Search..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            autoFocus
                        />
                    </div>
                )}
                {filteredItems.map((item, i) => {
                    // Find the parent group header for this item
                    let parentHeaderValue: string | number | boolean | null = null;
                    if (!item.isGroupHeader) {
                        for (let j = i - 1; j >= 0; j--) {
                            if (filteredItems[j].isGroupHeader) {
                                parentHeaderValue = filteredItems[j].value;
                                break;
                            }
                        }
                    }

                    // Skip non-header items in collapsed groups (but not while searching)
                    if (
                        !searchQuery &&
                        !item.isGroupHeader &&
                        parentHeaderValue !== null &&
                        collapsedGroups.has(parentHeaderValue)
                    ) {
                        return null;
                    }

                    if (item.isGroupHeader) {
                        const isCollapsed = collapsedGroups.has(item.value);
                        return (
                            <div
                                key={String(item.value)}
                                className={classNames(
                                    theme.inactiveItemClasses,
                                    "!cursor-pointer select-none",
                                    item.className
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setCollapsedGroups((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(item.value)) {
                                            next.delete(item.value);
                                        } else {
                                            next.add(item.value);
                                        }
                                        return next;
                                    });
                                }}
                                onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                                onMouseUp={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                <span
                                    className={twClassNames(
                                        "flex items-center justify-between gap-2 truncate",
                                        item.menuItemParentClassName || ""
                                    )}
                                >
                                    <span className="flex items-baseline gap-2">
                                        {item.icon && <i className={item.icon}></i>}
                                        {item.text || item.value}
                                    </span>
                                    <i
                                        className={classNames(
                                            "far fa-chevron-down scale-75 text-gray-400 transition-transform duration-150",
                                            isCollapsed && "-rotate-90"
                                        )}
                                    ></i>
                                </span>
                            </div>
                        );
                    }

                    return (
                        <Menu.Item key={i} disabled={item.disabled || false}>
                            {({ active }) => (
                                <div
                                    className={classNames(
                                        {
                                            [theme.activeItemClasses]: active,
                                            [theme.inactiveItemClasses]: !active,
                                            [theme.disabledItemClasses]: item.disabled
                                        },
                                        item.className
                                    )}
                                    onClick={(e: any) => onClickItem(item, e)}
                                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                                    onMouseUp={(e: React.MouseEvent) => e.stopPropagation()}
                                    onMouseEnter={() => {
                                        props.onItemMouseEnter?.(item.value);
                                    }}
                                    onMouseLeave={() => {
                                        props.onItemMouseLeave?.(item.value);
                                    }}
                                >
                                    <DropdownItemComponent
                                        value={item.value}
                                        text={item.text}
                                        icon={item.icon}
                                        disabled={item.disabled}
                                        menuItemParentClassName={item.menuItemParentClassName}
                                    />
                                </div>
                            )}
                        </Menu.Item>
                    );
                })}
            </Menu.Items>
        </Transition>
    );

    return (
        <Menu
            as="div"
            className={"headless-menu group flex flex-col gap-1" + " " + (props.className || "")}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        >
            {props.label && (
                <div className="inputLabel text-sm font-medium text-gray-700">{props.label}</div>
            )}
            {props.iconOnly ? (
                <Menu.Button
                    className={props.buttonClassName ?? undefined}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    ref={setReferenceElement}
                >
                    <i className={theme.menuButtonIconClasses}></i>
                </Menu.Button>
            ) : (
                <Menu.Button
                    as="div"
                    className={classNames(
                        customButtonClasses || theme.menuButtonClasses,
                        props.error ? "!border-pink-500 !text-pink-700" : "",
                        props.disabled ? "cursor-not-allowed" : "",
                        "justify-between"
                    )}
                    disabled={props.disabled}
                    ref={setReferenceElement}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                    onMouseUp={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    {props.titlePrefix && (
                        <span className={props.titlePrefixClassName || ""}>
                            {props.titlePrefix}
                        </span>
                    )}
                    {props.title && (
                        <span
                            className={classNames(
                                props.buttonClassName ? null : theme.menuButtonTextClasses,
                                props.error ? "!border-pink-500 !text-pink-700" : "",
                                props.disabled ? "cursor-not-allowed" : ""
                            )}
                        >
                            <DropdownItemComponent
                                {...{
                                    value: props.autoSetTitle
                                        ? (title as string)
                                        : (props.title as string),
                                    text: props.title || "",
                                    icon: props.icon,
                                    disabled: props.disabled
                                }}
                            />
                        </span>
                    )}
                    <i className={theme.menuButtonIconClasses}></i>
                </Menu.Button>
            )}

            {props.usePortal
                ? createPortal(
                      <div className="headless-menu" style={{ display: "contents" }}>
                          {dropdownMenu}
                      </div>,
                      document.body
                  )
                : dropdownMenu}
        </Menu>
    );
}
